// Library methods
import { useTranslation } from "react-i18next";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import CustomSwitch from "src/components/shared/inputs/CustomSwitch/CustomSwitch";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";

// Styles
import {
  CustomMultiLineTextFieldProps,
  getSurfaceTokens,
  ModalCustomCancel,
  ModalCustomConfirm,
} from "src/styles/mui/muiStylesHelper";

// Contexts
import { ReportContext } from "src/contexts/reports/ReportContext";
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";

// Constants
import {
  PatientConditionType,
  patientConditionTypes,
} from "src/constants/enums";

// Utils
import { sortByDate } from "src/utils/date/dateHelper";

type AddCommentModalType = {
  open: boolean;
  onClose: () => void;
  submitComment: (commentData: {
    content: string;
    conditions?: string | undefined;
  }) => Promise<void>;
};

const MAX_COMMENT_LENGTH = 200;

const AddCommentModal = ({
  open,
  onClose,
  submitComment,
}: AddCommentModalType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { inputBorder } = getSurfaceTokens(theme);

  const {
    exam,
    refetchExamComment,
    refetchProgressionAnalysisComment,
    getReportInfoData,
  } = useContext(ReportContext);
  const { commentData } = useContext(ReportSectionContext);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const sortedComments = useMemo(
    () => sortByDate(commentData, "creationDate"),
    [commentData]
  );

  const latestComment = useMemo(
    () => sortedComments?.[0] ?? null,
    [sortedComments]
  );

  const sortedPatientConditions = useMemo(
    () =>
      patientConditionTypes
        .map((condition) => condition)
        .sort((a, b) => t(a).localeCompare(t(b))),
    [t]
  );

  // default toggle state is everything set to false
  const defaultToggles: { [key in PatientConditionType]?: boolean } = useMemo(
    () =>
      sortedPatientConditions.reduce(
        (acc, curr) => ({ ...acc, [curr]: false }),
        {}
      ),
    [sortedPatientConditions]
  );

  // toggle state, based on the conditions set on the latest comment
  const latestToggles: { [key in PatientConditionType]?: boolean } = useMemo(
    () =>
      sortedPatientConditions.reduce(
        (acc, curr) => ({
          ...acc,
          [curr]: Boolean(latestComment?.conditions?.includes(curr)),
        }),
        {}
      ),
    [latestComment, sortedPatientConditions]
  );

  const [commentValue, setCommentValue] = useState("");
  const [toggleState, setToggleState] = useState<{
    [key in PatientConditionType]?: boolean;
  }>({ ...defaultToggles });

  // reset toggles every time the modal is opened
  useEffect(() => {
    if (open) {
      setToggleState({ ...latestToggles });
    }
  }, [open, latestToggles]);

  const hasTogglesChanged = useMemo(
    () =>
      sortedPatientConditions.some(
        (condition) => toggleState[condition] !== latestToggles[condition]
      ),
    [toggleState, latestToggles, sortedPatientConditions]
  );

  const isSubmitDisabled = useMemo(
    () => (!commentValue || !commentValue?.trim()) && !hasTogglesChanged,
    [commentValue, hasTogglesChanged]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setCommentValue(event.target.value);
    },
    []
  );

  const handleToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setToggleState({
        ...toggleState,
        [event.target.name]: checked,
      });
    },
    [toggleState]
  );

  const resetData = useCallback(() => {
    setToggleState({ ...defaultToggles });
    setCommentValue("");
  }, [defaultToggles]);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);

    // extract conditions except the last item which means 'other condition'.
    const keys = Object.keys(toggleState) as Array<PatientConditionType>;
    const conditions = keys
      .filter((key) => toggleState[key])
      .map((key) => `${key}`);

    // replace one or more newlines with a single space
    const replacedComment = commentValue?.replace(/\n+/g, " ");
    const commentData = {
      content: replacedComment,
      conditions: conditions?.length
        ? `[${conditions.map((condition) => `"` + condition + `"`)}]`
        : undefined,
    };

    try {
      await submitComment(commentData);

      // display new comments on the page
      if (exam) {
        await refetchExamComment(exam);
      } else {
        await refetchProgressionAnalysisComment();
      }

      // regenerate reports with the new comment
      await getReportInfoData();

      // scroll to comments list
      setTimeout(() => {
        document
          ?.getElementById("comments-list")
          ?.scrollIntoView({ behavior: "smooth" });
      }, 100);

      // reset and close the modal
      resetData();
      onClose();
    } catch (error) {
      // do nothing
    } finally {
      setIsSubmitting(false);
    }
  }, [
    toggleState,
    commentValue,
    exam,
    getReportInfoData,
    onClose,
    refetchExamComment,
    refetchProgressionAnalysisComment,
    resetData,
    submitComment,
  ]);

  return (
    <DefaultModal
      isOpen={open}
      onClose={() => {
        resetData();
        onClose();
      }}
      title={t("Add Comment")}
      maxWidth="sm"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
          }}
        >
          <Button
            onClick={() => {
              resetData();
              onClose();
            }}
            disabled={isSubmitting}
            sx={ModalCustomCancel}
          >
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            sx={ModalCustomConfirm}
            disabled={isSubmitDisabled || isSubmitting}
          >
            {t("Add")}
          </Button>
        </Grid>
      }
    >
      <Grid
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          padding: "16px",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <Typography sx={{ fontSize: "14px" }}>{t("Comment Text")}</Typography>
          <TextField
            value={commentValue}
            placeholder={t("Enter a value")}
            autoFocus
            id="add-comment-modal-input"
            type="text"
            fullWidth
            multiline
            minRows={4}
            maxRows={4}
            variant="outlined"
            color="warning"
            onChange={handleChange}
            inputProps={{
              maxLength: MAX_COMMENT_LENGTH,
              sx: {
                "&::placeholder": {
                  color: inputBorder,
                  opacity: 1,
                },
              },
            }}
            InputProps={{
              sx: {
                ...CustomMultiLineTextFieldProps(theme),
                padding: "16px",
                ".MuiOutlinedInput-notchedOutline": {
                  border: "1px solid #5B6D7A",
                },
              },
            }}
          />
          <Typography
            textAlign="right"
            color={
              commentValue?.length === MAX_COMMENT_LENGTH
                ? "#E10C0C"
                : "#5B6D7A"
            }
          >
            {`${commentValue?.length ?? 0}/${MAX_COMMENT_LENGTH}`}
          </Typography>
        </Box>

        <Grid container display="flex" gap="4px">
          {sortedPatientConditions.map((condition) => (
            <FormControlLabel
              key={condition}
              label={<Typography fontSize="14px">{t(condition)}</Typography>}
              labelPlacement="end"
              control={
                <CustomSwitch
                  checked={toggleState?.[condition]}
                  onChange={handleToggle}
                  name={condition}
                />
              }
            />
          ))}
        </Grid>
      </Grid>
    </DefaultModal>
  );
};

export default AddCommentModal;
